// Consent for an application that wants to connect (T-107, US-500): which app, which
// permissions, in which company. Approving issues a one-time code and returns to the app.

import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Navigate, useSearchParams } from "react-router";

import { api, problemText, type Me } from "../api";
import { Button, Card, ErrorText, Logo } from "../components/ui";
import { t, type MessageKey } from "../i18n";

interface ConsentInfo {
  request_id: string;
  client_name: string;
  client_id: string;
  client_uri: string | null;
  redirect_host: string;
  verified_domain: boolean;
  scopes: { name: string; description: string }[];
  companies: { id: string; name: string; role: string; scopes: string[] }[];
  active_company_id: string | null;
}

export function Consent({ me }: { me: Me | null }) {
  const [params] = useSearchParams();
  const requestId = params.get("request") ?? "";
  const info = useQuery({
    queryKey: ["consent", requestId],
    queryFn: () => api.get<ConsentInfo>(`/auth/oauth-requests/${requestId}`),
    enabled: Boolean(me && requestId),
    retry: false,
  });
  const [company, setCompany] = useState<string | null>(null);

  const decide = useMutation({
    mutationFn: (approve: boolean) =>
      approve
        ? api.post<{ redirect: string }>(`/auth/oauth-requests/${requestId}/approve`, { company_id: chosen })
        : api.post<{ redirect: string }>(`/auth/oauth-requests/${requestId}/deny`),
    onSuccess: (result) => window.location.assign(result.redirect),
  });

  if (!me) {
    // Sign in first, then come back to this page.
    const next = `/consent?request=${encodeURIComponent(requestId)}`;
    return <Navigate to={`/sign-in?next=${encodeURIComponent(next)}`} replace />;
  }

  const data = info.data;
  const chosen =
    company ??
    data?.companies.find((c) => c.id === data.active_company_id)?.id ??
    data?.companies[0]?.id ??
    null;
  const chosenCompany = data?.companies.find((c) => c.id === chosen);

  return (
    <div className="grid min-h-full place-items-center px-4 py-12">
      <div className="flex w-full max-w-[460px] flex-col gap-5">
        <Logo className="h-7" />
        {!requestId && <ErrorText>{t("consent.missing")}</ErrorText>}
        {info.isLoading && <p className="m-0 text-steel">{t("common.working")}</p>}
        {info.isError && (
          <ErrorText>{problemText(info.error)}</ErrorText>
        )}
        {data && (
          <>
            <div className="flex flex-col gap-1.5">
              <h1 className="m-0 text-2xl font-semibold tracking-tight">
                {t("consent.title", { app: data.client_name })}
              </h1>
              <p className="m-0 text-steel">
                {t("consent.lead", { host: data.redirect_host })}
                {data.verified_domain ? ` ${t("consent.verified")}` : ""}
              </p>
            </div>

            {data.companies.length > 1 && (
              <Card title={t("consent.company")}>
                <div className="flex flex-col gap-1">
                  {data.companies.map((c) => (
                    <label key={c.id} className="flex cursor-pointer items-center gap-2.5 rounded-[6px] px-2 py-1.5 hover:bg-panel">
                      <input type="radio" name="company" checked={c.id === chosen} onChange={() => setCompany(c.id)} />
                      <span className="flex-1">{c.name}</span>
                      <span className="text-xs text-steel">{t(`role.${c.role}` as MessageKey)}</span>
                    </label>
                  ))}
                </div>
              </Card>
            )}

            <Card title={t("consent.permissions", { company: chosenCompany?.name ?? "" })}>
              <ul className="m-0 flex list-none flex-col gap-2 p-0">
                {data.scopes.map((scope) => {
                  const granted = chosenCompany?.scopes.includes(scope.name) ?? false;
                  return (
                    <li key={scope.name} className={`flex flex-col ${granted ? "" : "opacity-50"}`}>
                      <span>{scope.description}</span>
                      {!granted && <span className="text-xs text-steel">{t("consent.notForRole")}</span>}
                    </li>
                  );
                })}
              </ul>
            </Card>

            <p className="m-0 text-sm text-steel">{t("consent.footnote")}</p>
            {decide.isError && (
              <ErrorText>{problemText(decide.error)}</ErrorText>
            )}
            <div className="flex gap-3">
              <Button kind="primary" size="lg" disabled={!chosen || decide.isPending} onClick={() => decide.mutate(true)}>
                {t("consent.allow")}
              </Button>
              <Button size="lg" disabled={decide.isPending} onClick={() => decide.mutate(false)}>
                {t("consent.deny")}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function OAuthError() {
  const [params] = useSearchParams();
  return (
    <div className="grid min-h-full place-items-center px-4 py-12">
      <div className="flex w-full max-w-[460px] flex-col gap-4">
        <Logo className="h-7" />
        <h1 className="m-0 text-2xl font-semibold tracking-tight">{t("consent.errorTitle")}</h1>
        <ErrorText>{params.get("description") ?? t("common.error")}</ErrorText>
        <p className="m-0 text-sm text-steel">{t("consent.errorHint")}</p>
      </div>
    </div>
  );
}
